import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { api } from './api'
import { adoptHousehold, clearLocalData } from './db'
import { startSyncLoop } from './sync'

export interface SessionUser {
  id: string
  householdId: string
  username: string
  displayName: string
  /** El usuario del seed: puede dar de alta usuarios y hogares. */
  isAdmin?: boolean
}

interface SessionValue {
  user: SessionUser | null
  loading: boolean
  login: (username: string, password: string) => Promise<void>
  register: (input: {
    username: string
    password: string
    displayName: string
    inviteCode?: string
    householdName?: string
  }) => Promise<void>
  logout: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api
      .get<{ user: SessionUser | null }>('/api/auth/me')
      .then(async (data) => {
        if (data.user) await adoptHousehold(data.user.householdId)
        setUser(data.user)
      })
      .catch(() => {
        // Sin red al arrancar no es motivo para echar a nadie: la sesión vive
        // en una cookie y la app funciona con el espejo local.
      })
      .finally(() => setLoading(false))
  }, [])

  // El bucle de sincronización sólo tiene sentido con alguien dentro.
  useEffect(() => (user ? startSyncLoop() : undefined), [user])

  const login = useCallback(async (username: string, password: string) => {
    const data = await api.post<{ user: SessionUser }>('/api/auth/login', { username, password })
    await adoptHousehold(data.user.householdId)
    setUser(data.user)
  }, [])

  const register = useCallback<SessionValue['register']>(async (input) => {
    const data = await api.post<{ user: SessionUser }>('/api/auth/register', input)
    await adoptHousehold(data.user.householdId)
    setUser(data.user)
  }, [])

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => undefined)
    await clearLocalData()
    setUser(null)
  }, [])

  return (
    <SessionContext value={{ user, loading, login, register, logout }}>{children}</SessionContext>
  )
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) throw new Error('useSession fuera de SessionProvider')
  return value
}
